import type { StatusTone } from '../contracts/common';
import type { ProjectMemberRole, ProjectStatus } from '../contracts/projects';

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  planned: 'Planowany',
  active: 'Aktywny',
  on_hold: 'Wstrzymany',
  done: 'Zakończony',
  cancelled: 'Anulowany',
};

export const PROJECT_STATUS_TONES: Record<ProjectStatus, StatusTone> = {
  planned: 'neutral',
  active: 'accent',
  on_hold: 'warning',
  done: 'success',
  cancelled: 'neutral',
};

export const PROJECT_MEMBER_ROLE_LABELS: Record<ProjectMemberRole, string> = {
  lead: 'Prowadzi',
  member: 'Członek',
};

/** Zakładki listy projektów (Figma 07 Projekty). `services` = usługi klientów (moduł usług). */
export const PROJECT_LIST_TAB_LABELS = {
  active: 'Aktywne',
  services: 'Usługi',
  internal: 'Wewnętrzne',
  closed: 'Zakończone',
} as const;

/** Rodzaj rekordu w podpisie wiersza („REA · projekt”, „Fravity · wewnętrzny”). */
export const PROJECT_KIND_LABELS = { project: 'projekt', internal: 'wewnętrzny' } as const;

/**
 * Próg ostrzeżenia budżetu godzin (udział wykorzystania): „Blisko limitu”, pomarańczowy pasek i kafelek.
 * 80% jak w Figmie (05 Profil REA: 32 / 40 h na pomarańczowo) — DECISIONS, rozstrzygnięcia pytań z Fali 1.
 */
export const PROJECT_BUDGET_WARNING_RATIO = 0.8;

export interface ProjectStatusDisplayInput {
  status: ProjectStatus;
  isInternal: boolean;
  billableDefault: boolean;
  archivedAt?: Date | string | null;
  hoursBudgetSeconds?: number | null;
  usedSeconds?: number;
}

/**
 * Status projektu do wyświetlenia (kropka + etykieta). Kolor tylko, gdy coś znaczy (DESIGN §2):
 * archiwum → szary; otwarty projekt ponad budżetem → czerwony, blisko limitu → pomarańczowy;
 * aktywny projekt wewnętrzny nierozliczany → „Nierozliczany” (Figma); poza tym status projektu.
 */
export function projectStatusDisplay(p: ProjectStatusDisplayInput): { label: string; tone: StatusTone } {
  if (p.archivedAt) return { label: 'W archiwum', tone: 'neutral' };
  const open = p.status === 'planned' || p.status === 'active' || p.status === 'on_hold';
  const budget = p.hoursBudgetSeconds ?? 0;
  if (open && budget > 0) {
    const ratio = (p.usedSeconds ?? 0) / budget;
    if (ratio > 1) return { label: 'Ponad budżet', tone: 'danger' };
    if (ratio >= PROJECT_BUDGET_WARNING_RATIO) return { label: 'Blisko limitu', tone: 'warning' };
  }
  if (p.status === 'active' && p.isInternal && !p.billableDefault) return { label: 'Nierozliczany', tone: 'neutral' };
  return { label: PROJECT_STATUS_LABELS[p.status], tone: PROJECT_STATUS_TONES[p.status] };
}
